import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { catchError, map, Observable, throwError } from 'rxjs';

// HTTP options for application/x-www-form-urlencoded (matching AngularJS $http behavior)
const HTTP_OPTIONS = {
  headers: new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
  }),
  withCredentials: true
};

// ============================================
// INTERFACES / MODELS
// ============================================

export interface LevelOption {
  CODE_KEY: number;
  CODE_DESCRIPTION: string;
  CODE_GROUP?: string;
}

export interface ParentMeOption {
  PARENT_ME_KEY: number;
  PARENT_ME_NAME: string;
  LEVEL_GROUP?: string;
}

export interface GrossIncomeOption {
  ACCT_KEY: number;
  DISP_ACCT_CODE: string;
  ACCT_SUB_CATG?: string;
  GIE_KEY?: number;
  ACCT_DESC?: string;  // Short code like 'ASTEXP', 'INTEXP', 'RESEARCH' - used for Base Type API
}

export interface MapOption {
  CODE_KEY: number;
  CODE_DESCRIPTION: string;
  CODE_GROUP?: string;
}

export interface MethodOption {
  CODE_KEY: number;
  CODE_DESCRIPTION: string;
  IS_DEFAULT?: string;
}

export interface SystemAccountOption {
  ACCT_KEY: string;
  DESCRIPTION: string;
  ACCT?: string;
}

export interface LocalAccountOption {
  ACCT_KEY: string;
  ACCT_DESC: string;
  FROM_ACCT_KEY?: string;
}

export interface ScheduleMOption {
  SCHDM_TEMPLATE_ID: string;
  SCHDM_DESC: string;
}

export interface BaseCodeOption {
  CODE_KEY: number;
  CODE_DESCRIPTION: string;
  CODE_DESC?: string;
  CODE_GROUP?: string;
  CODE_UPDATED_DESCRIPTION?: string;
}

export interface AttribMastOption {
  ATTRIB_KEY: number;
  ATTRIB_NAME: string;
}

export interface TransCategoryRowData {
  TRANS_CATG_KEY?: number;
  TRANS_CATG_NAME?: string;
  LEVEL_KEY?: number;
  LEVEL_DESC?: string;
  LEVEL1?: string;
  ME_KEY?: number;
  GIE_KEY?: number;
  DISP_ACCT_CODE?: string;
  MAP_KEY?: number;
  MAP_DESC?: string;
  MAPTO?: string;
  METHOD_KEY?: number;
  METHOD_CODE?: string;
  PERCENTAGE?: number;
  IS_DELETE?: string;
  BASE_FDII?: string;
  BASE_BASKET?: string;
  BASE_TYPE?: string;
  BASE_LEVEL?: string;
  BASE_ENTITY_LEVEL?: string;
  OBJECT_ID?: number;
  ACCT_KEY?: number;
  ACCOUNT_SCHEDULE_M?: string;
  SYS_ACCT_KEY?: string;
  LOCAL_ACCT_KEY?: string;
  SCENARIO_KEY?: number;
  COMBINATION_KEY?: number;
}

export interface LockStatusResponse {
  LOCK_STATUS: string;
  ADMIN_STATUS: string;
}

export interface SaveTransCategoryPayload {
  type: 'I' | 'U' | 'D';
  trans_catg_key: number | null;
  trans_catg_name: string;
  gross_acct_key: number;
  level: number;
  map_key: number;
  me_key?: number;
  method_key?: number | null;
  GIE_KEY?: number;
  is_core: string;
  is_ftc: string;
  is_changed: string;
  percentage?: number;
  is_all_sys_acct?: string | null;
  sys_acct_key?: string | null;
  is_all_local_acct?: string | null;
  local_acct_key?: string | null;
  is_all_m_temp?: string | null;
  m_template_key?: string | null;
  attrib_value?: string;
  BASE_FDII_DATA?: string | null;
  BASE_BASKET_DATA?: string | null;
  BASE_TYPE_DATA?: string | null;
}

// ============================================
// SERVICE
// ============================================

@Injectable({
  providedIn: 'root'
})
export class ExpenseTransAddEditService {

  private urls = {
    lockCheck: '/loadJsonObject?action_code=n10cpv',
    levelAndMapDetails: '/loadJsonObject?action_code=0gc5cq',
    parentMeDetails: '/loadJsonObject?action_code=hmkia6',
    grossIncomeDetails: '/loadJsonObject?action_code=ug1a8d',
    systemAccounts: '/loadJsonObject?action_code=05r78u',
    localAccounts: '/loadJsonObject?action_code=n1ez0t',
    scheduleMOptions: '/loadJsonObject?action_code=mcf9nk',
    attribMast: '/loadJsonObject?action_code=2nk5nz',
    codeMastData: '/loadJsonObject?action_code=31hizx',
    baseType: '/loadJsonObject?action_code=6xgttx',
    methodOptions: '/loadJsonObject?action_code=lwtsx1',
    allTransCategory: '/loadJsonObject?action_code=9aszao',
    accountInfo: '/loadJsonObject?action_code=30cny0',
    scheduleMInfo: '/loadJsonObject?action_code=lrs0ke',
    baseFdiiEdit: '/loadJsonObject?action_code=a8rd93',
    baseBasketEdit: '/loadJsonObject?action_code=z6htpi',
    baseTypeEdit: '/loadJsonObject?action_code=6kzmd7',
    save: '/saveJsonObject?action_code=4x3jcm'
  };

  private durationInSeconds = 4;

  // Global params from AngularJS (tax_year, scenario, jcd_key, screen_key, etc.)
  private globalParams: any = {};
  // Filter params from AngularJS workspace filters
  private filterParams: any = {};

  constructor(
    private http: HttpClient,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService
  ) {}

  // ============================================
  // SET GLOBAL/FILTER PARAMS (called from component)
  // ============================================
  
  setGlobalParams(globalParams: any): void {
    this.globalParams = globalParams || {};
  }

  setFilterParams(filterParams: any): void {
    this.filterParams = filterParams || {};
  }

  // ============================================
  // SNACKBAR
  // ============================================

  openSnackBar(message: string, panelClass: string = '') {
    this.gtwSnackbar.openSnackBar(message, (panelClass || '') as any, true, this.durationInSeconds);
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * Check if tax year is locked
   */
  checkLockStatus(baseUrl: string, params: any): Observable<LockStatusResponse[]> {
    const url = `${baseUrl}${this.urls.lockCheck}`;
    const body = this.buildFormData({
      p_consol_group_key: '1'
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject;
        }
        return [];
      }),
      catchError(error => {
        console.error('Error checking lock status:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Level and Map dropdown options
   */
  getLevelAndMapDetails(baseUrl: string): Observable<any[]> {
    const url = `${baseUrl}${this.urls.levelAndMapDetails}`;
    const body = this.buildFormData({});
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching level/map details:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Parent ME / Filing Group / ME options
   */
  getParentMeDetails(baseUrl: string): Observable<ParentMeOption[]> {
    const url = `${baseUrl}${this.urls.parentMeDetails}`;
    const body = this.buildFormData({});
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching parent ME details:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Gross Income/Expense dropdown options
   */
  getGrossIncomeDetails(baseUrl: string): Observable<GrossIncomeOption[]> {
    const url = `${baseUrl}${this.urls.grossIncomeDetails}`;
    const body = this.buildFormData({});
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching gross income details:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get System Account options
   */
  getSystemAccounts(baseUrl: string, acctKey: number, grFlag: string = 'E'): Observable<SystemAccountOption[]> {
    const url = `${baseUrl}${this.urls.systemAccounts}`;
    const body = this.buildFormData({
      acct_key: acctKey.toString(),
      gr_flag: grFlag
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching system accounts:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Local Account options
   */
  getLocalAccounts(baseUrl: string, acctKeyStr: string, meKey: number | null): Observable<LocalAccountOption[]> {
    const url = `${baseUrl}${this.urls.localAccounts}`;
    // IMPORTANT: Pass 'null' as string when meKey is null (matching AngularJS behavior)
    // AngularJS sends me_key: null which becomes 'null' string in the request
    const body = this.buildFormData({
      acct_key: acctKeyStr,
      me_key: meKey !== null ? meKey.toString() : 'null'
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching local accounts:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Schedule M options
   */
  getScheduleMOptions(baseUrl: string): Observable<ScheduleMOption[]> {
    const url = `${baseUrl}${this.urls.scheduleMOptions}`;
    const body = this.buildFormData({});
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching schedule M options:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Attrib Mast data
   */
  getAttribMast(baseUrl: string): Observable<AttribMastOption[]> {
    const url = `${baseUrl}${this.urls.attribMast}`;
    const body = this.buildFormData({});
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching attrib mast:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Code Mast Data (Base FDII, Basket, Type, Level, Entity)
   */
  getCodeMastData(baseUrl: string, scenario: number, jcdKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.codeMastData}`;
    const body = this.buildFormData({
      cc_key: '',
      p_scenario: scenario.toString(),
      p_jcd_key: jcdKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching code mast data:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Base Type options
   */
  getBaseType(baseUrl: string, gieName: string, scenario: number, jcdKey: number): Observable<BaseCodeOption[]> {
    const url = `${baseUrl}${this.urls.baseType}`;
    const body = this.buildFormData({
      gie_name: gieName,
      p_scenario: scenario.toString(),
      p_jcd_key: jcdKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching base type:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Method options
   */
  getMethodOptions(baseUrl: string, gieKey: number, scenario: number): Observable<MethodOption[]> {
    const url = `${baseUrl}${this.urls.methodOptions}`;
    const body = this.buildFormData({
      gie_key: gieKey.toString(),
      p_scenario: scenario.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching method options:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get All Trans Category (except Exp)
   */
  getAllTransCategory(baseUrl: string, scenario: number, jcdKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.allTransCategory}`;
    const body = this.buildFormData({
      p_scenario: scenario.toString(),
      p_jcd_key: jcdKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching all trans category:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Account Information (for edit mode - by Account)
   */
  getAccountInfo(baseUrl: string, transCatgKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.accountInfo}`;
    const body = this.buildFormData({
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching account info:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Schedule M Information (for edit mode - by Schedule M)
   */
  getScheduleMInfo(baseUrl: string, transCatgKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.scheduleMInfo}`;
    const body = this.buildFormData({
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching schedule M info:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Base FDII edit data
   */
  getBaseFdiiEdit(baseUrl: string, ssoId: string, taxYear: number, scenario: number, transCatgKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.baseFdiiEdit}`;
    const body = this.buildFormData({
      sso_id: ssoId,
      tax_year: taxYear.toString(),
      scenario: scenario.toString(),
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching base FDII edit:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Base Basket edit data
   */
  getBaseBasketEdit(baseUrl: string, ssoId: string, taxYear: number, scenario: number, transCatgKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.baseBasketEdit}`;
    const body = this.buildFormData({
      sso_id: ssoId,
      tax_year: taxYear.toString(),
      scenario: scenario.toString(),
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching base basket edit:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Base Type edit data
   */
  getBaseTypeEdit(baseUrl: string, ssoId: string, taxYear: number, scenario: number, transCatgKey: number): Observable<any[]> {
    const url = `${baseUrl}${this.urls.baseTypeEdit}`;
    const body = this.buildFormData({
      sso_id: ssoId,
      tax_year: taxYear.toString(),
      scenario: scenario.toString(),
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching base type edit:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Save Trans Category
   * buildHttpParamsForSave merges globalParams + filterParams + these specific params.
   * scenarioCode / is_issue_key_enabled are only added if explicitly provided so they
   * don't override values already present in globalParams/filterParams.
   */
  saveTransCategory(
    baseUrl: string,
    payload: SaveTransCategoryPayload,
    otherInfo: { sso_id: string; screen_key: number; scenarioCode?: string; is_issue_key_enabled?: string }
  ): Observable<any> {
    const url = `${baseUrl}${this.urls.save}`;

    const params: any = {
      clob_data: JSON.stringify([payload]),
      clob_settings: JSON.stringify(payload),
      sso_id: otherInfo.sso_id,
      screen_key: otherInfo.screen_key.toString(),
      process_name: 'Trans_SAVE',
    };

    if (otherInfo.scenarioCode) {
      params.scenarioCode = otherInfo.scenarioCode;
    }
    if (otherInfo.is_issue_key_enabled) {
      params.is_issue_key_enabled = otherInfo.is_issue_key_enabled;
    }

    const body = this.buildHttpParamsForSave(params);

    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => {
        console.error('Error saving trans category:', error);
        return throwError(() => error);
      })
    );
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Build FormData with merged params (like AngularJS JsonObjectFactory.getJSONObj)
   * Merges: filterParams + globalParams + provided params
   * This mimics the behavior:
   *   var paramsMerge = _.merge({}, filterParams, _params);
   *   var params = _.merge({}, GlobalService.globalParams, paramsMerge);
   * 
   * IMPORTANT: We merge into a single object first to avoid duplicate keys
   * NOTE: Empty strings are filtered out for dropdown requests
   * Returns HttpParams for application/x-www-form-urlencoded content type (matching AngularJS $http behavior)
   */
  private buildFormData(params: { [key: string]: string }): HttpParams {
    // Merge all params into single object (later values override earlier)
    // Order: globalParams < filterParams < specific params
    const mergedParams: { [key: string]: string } = {};
    
    // First add globalParams (jcd_key, screen_key, activity_key, etc.)
    if (this.globalParams) {
      Object.keys(this.globalParams).forEach(key => {
        const value = this.globalParams[key];
        if (value !== undefined && value !== null && value !== '') {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Then add filterParams (tax_year, scenario from workspace filters) - overrides globalParams
    if (this.filterParams) {
      Object.keys(this.filterParams).forEach(key => {
        const value = this.filterParams[key];
        if (value !== undefined && value !== null && value !== '') {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Finally add specific params - overrides everything
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        mergedParams[key] = params[key];
      }
    });
    
    // Build HttpParams from merged object (no duplicates)
    let httpParams = new HttpParams();
    Object.keys(mergedParams).forEach(key => {
      httpParams = httpParams.set(key, mergedParams[key]);
    });
    
    return httpParams;
  }

  /**
   * Build HttpParams for SAVE operation - preserves empty strings
   * This is needed because save operation requires empty strings for issue_id, issue_short_desc, etc.
   * Returns HttpParams for application/x-www-form-urlencoded content type
   * Used ONLY for save requests
   */
  private buildHttpParamsForSave(params: { [key: string]: string }): HttpParams {
    const mergedParams: { [key: string]: string } = {};
    
    // First add globalParams - include empty strings for save
    if (this.globalParams) {
      Object.keys(this.globalParams).forEach(key => {
        const value = this.globalParams[key];
        if (value !== undefined && value !== null) {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Then add filterParams - include empty strings for save
    if (this.filterParams) {
      Object.keys(this.filterParams).forEach(key => {
        const value = this.filterParams[key];
        if (value !== undefined && value !== null) {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Finally add specific params - overrides everything
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        mergedParams[key] = params[key];
      }
    });
    
    // Build HttpParams from merged object
    let httpParams = new HttpParams();
    Object.keys(mergedParams).forEach(key => {
      httpParams = httpParams.set(key, mergedParams[key]);
    });
    
    return httpParams;
  }
}
